import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "strassenverzeichnis";

export type Strasse = {
  readonly id: string;
  readonly name: string;
  readonly hausnummer?: string;
  readonly postleitzahl?: string;
  readonly ort?: string;
  readonly ortsteil?: string;
  readonly latitude?: number;
  readonly longitude?: number;
};

/**
 * Filters the street directory for a free-text address search. The query is split into words
 * (spaces and commas), and every word must match some part of the entry — so "Hauptstraße 5",
 * "Hauptstraße Hildesheim" or a previously selected "Hauptstraße, 5, 31134 Hildesheim" all find
 * the street. Text parts (street, town, district) match anywhere; house number and postal code
 * must start with the word, so "5" doesn't hit "15" or "31157". A house-number-like word
 * ("7", "12a") also matches street-level entries without a house number, since most directories
 * only list streets. Entries whose house number equals one of the words are listed first.
 */
const HAUSNUMMER = /^\d{1,4}[a-z]?$/;

/** The first house-number-like word of a free-text address query, e.g. "7" in "Hauptstraße 7". */
export function extractHausnummer(query: string): string | undefined {
  return query.split(/[\s,]+/).find((w) => HAUSNUMMER.test(w.toLowerCase()));
}

export function filterStrassen(
  strassen: ReadonlyArray<Strasse>,
  query: string,
  limit = 50,
): Strasse[] {
  const words = query
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(Boolean);
  if (!words.length) return strassen.slice(0, limit);

  const matches = strassen.filter((s) => {
    const texts = [s.name, s.ort, s.ortsteil]
      .filter(Boolean)
      .map((v) => v!.toLowerCase());
    const numbers = [s.hausnummer, s.postleitzahl]
      .filter(Boolean)
      .map((v) => v!.toLowerCase());
    return words.every(
      (w) =>
        texts.some((t) => t.includes(w)) ||
        numbers.some((n) => n.startsWith(w)) ||
        (!s.hausnummer && HAUSNUMMER.test(w)),
    );
  });

  const exactHausnummer = (s: Strasse) =>
    !!s.hausnummer && words.includes(s.hausnummer.toLowerCase());
  return matches
    .toSorted((a, b) => Number(exactHausnummer(b)) - Number(exactHausnummer(a)))
    .slice(0, limit);
}

function useStrassenverzeichnisSyncModule(): SyncModule<
  ReadonlyArray<Strasse>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // The street directory is a new-Datenportal-only resource — the legacy
      // portal has no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchStrassenverzeichnisNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchStrassenverzeichnisNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Strasse>> {
  const response = await fetch(`${baseUrl}/api/device/sync/streets`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.streets ?? []).map(parseStrasseNew);
}

function parseStrasseNew(entry: any): Strasse {
  return {
    id: entry.id,
    name: entry.name,
    hausnummer: entry.houseNumber || undefined,
    postleitzahl: entry.postalCode || undefined,
    ort: entry.city || undefined,
    ortsteil: entry.district || undefined,
    latitude: entry.latitude ?? undefined,
    longitude: entry.longitude ?? undefined,
  };
}

export function useStrassenverzeichnisSync() {
  const module = useStrassenverzeichnisSyncModule();
  useSyncModuleRegistration(module);
}

export function useStrassenverzeichnis(): ReadonlyArray<Strasse> {
  return useSyncData(syncModuleId) ?? [];
}
